import type { Environment, User } from '@fleequid-secrets/shared';
import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useOutside } from '@/lib/hooks';
import { Link, navigate } from '@/lib/router';
import { Brand } from './Brand';
import { Icon } from './Icon';

export type ModuleName = 'vault' | 'compare' | 'activity';

// The modules, as pages. G then the letter goes there from the keyboard.
export const MODULES: ReadonlyArray<{ name: ModuleName; label: string; key: string }> = [
    { name: 'vault', label: 'Variables', key: 'v' },
    { name: 'compare', label: 'Compare', key: 'c' },
    { name: 'activity', label: 'Activity', key: 'a' },
];

// The environment on screen: a pill in its colour (the edge across the window is the same colour), and the menu that
// switches it. E cycles from the keyboard.
function EnvPicker(props: { current: Environment | null; environments: readonly Environment[]; onEnv: (slug: string) => void }): ReactNode {
    const [open, setOpen] = useState(false);
    const root = useOutside(open, () => setOpen(false));

    return (
        <div className="envpick" ref={root}>
            <button className="envpill" onClick={() => setOpen(!open)} title="The environment on screen (E switches)">
                <span className="edot" />
                <span className="elabel">{props.current?.name ?? 'Environments'}</span>
                {props.current?.protected && <Icon name="lock" size={13} className="lock" />}
                {props.environments.length > 1 && <Icon name="chevron" size={14} />}
            </button>
            {open && props.environments.length > 0 && (
                <div className="menu float">
                    {props.environments.map((environment) => (
                        <button
                            key={environment.id}
                            className={environment.id === props.current?.id ? 'on' : ''}
                            onClick={() => {
                                setOpen(false);
                                props.onEnv(environment.slug);
                            }}
                        >
                            <i className="mdot" style={{ background: environment.color }} />
                            {environment.name}
                            {environment.protected && <Icon name="lock" size={13} />}
                            {environment.id === props.current?.id ? (
                                <span className="check">
                                    <Icon name="check" size={15} />
                                </span>
                            ) : (
                                <span className="r">{environment.sections.reduce((sum, section) => sum + section.variableCount, 0)}</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Who is signed in, and what they reach from anywhere: the team and the environments (admins), their password, the
// shortcuts, signing out.
function UserMenu(props: { user: User; onShortcuts: () => void; onPassword: () => void; onLogout: () => void }): ReactNode {
    const [open, setOpen] = useState(false);
    const root = useOutside(open, () => setOpen(false));
    const name = props.user.name || props.user.email.split('@')[0] || props.user.email;
    const pick = (action: () => void) => () => {
        setOpen(false);
        action();
    };

    return (
        <div className="usermenu" ref={root}>
            <button className={`avatar ${open ? 'on' : ''}`} title={props.user.email} onClick={() => setOpen(!open)}>
                {name.slice(0, 2).toUpperCase()}
            </button>
            {open && (
                <div className="menu umenu right float">
                    <div className="who">
                        <b>{name}</b>
                        <span>{props.user.email}</span>
                        <span className="role">{props.user.role === 'admin' ? 'Admin' : 'Member'}</span>
                    </div>
                    {props.user.role === 'admin' && (
                        <>
                            <button onClick={pick(() => navigate('/environments'))}>
                                <Icon name="layers" size={16} /> Environments
                            </button>
                            <button onClick={pick(() => navigate('/team'))}>
                                <Icon name="users" size={16} /> Team
                            </button>
                        </>
                    )}
                    <button onClick={pick(props.onPassword)}>
                        <Icon name="key" size={16} /> Change my password
                    </button>
                    <button onClick={pick(props.onShortcuts)}>
                        <Icon name="keyboard" size={16} /> Keyboard shortcuts
                        <span className="kbd" style={{ marginLeft: 'auto' }}>
                            ?
                        </span>
                    </button>
                    <div className="sep" />
                    <button className="danger" onClick={props.onLogout}>
                        <Icon name="logout" size={16} /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

export function TopBar(props: {
    active: ModuleName | null;
    paths: Record<ModuleName, string>;
    current: Environment | null;
    environments: readonly Environment[];
    onEnv: (slug: string) => void;
    user: User;
    onFind: () => void;
    onLogout: () => void;
    onShortcuts: () => void;
    onPassword: () => void;
}): ReactNode {
    // The pill under the active module slides to the next one: placed on the DOM, first without a transition.
    const nav = useRef<HTMLElement>(null);
    const mark = useRef<HTMLSpanElement>(null);
    useLayoutEffect(() => {
        const element = nav.current;
        const pill = mark.current;
        if (!element || !pill) {
            return;
        }
        const place = (): void => {
            const on = element.querySelector<HTMLElement>('a.on');
            pill.style.opacity = on ? '1' : '0';
            if (on) {
                pill.style.transform = `translateX(${on.offsetLeft}px)`;
                pill.style.width = `${on.offsetWidth}px`;
            }
        };
        place();
        const ready = setTimeout(() => {
            pill.dataset.ready = '1';
        }, 60);
        const observer = new ResizeObserver(place);
        observer.observe(element);

        return () => {
            clearTimeout(ready);
            observer.disconnect();
        };
    }, [props.active]);

    return (
        <header className="topbar">
            <Link to={props.paths.vault} className="brand-link" title="Variables">
                <Brand />
            </Link>
            <EnvPicker current={props.current} environments={props.environments} onEnv={props.onEnv} />
            <nav className="modules" ref={nav}>
                <span className="navline" ref={mark} />
                {MODULES.map((module) => (
                    <Link
                        key={module.name}
                        to={props.paths[module.name]}
                        className={module.name === props.active ? 'on' : ''}
                        title={`G then ${module.key.toUpperCase()}`}
                    >
                        {module.label}
                    </Link>
                ))}
            </nav>
            <span className="spacer" />
            <button className="find" onClick={props.onFind} title="Find a variable, a section, or a command (⌘K)">
                <Icon name="search" size={15} />
                <span>Find a variable</span>
                <span className="kbd">⌘K</span>
            </button>
            <UserMenu user={props.user} onShortcuts={props.onShortcuts} onPassword={props.onPassword} onLogout={props.onLogout} />
        </header>
    );
}

export const envStyle = (environment: { color: string } | null): CSSProperties =>
    ({ ['--env' as string]: environment?.color ?? 'var(--line-strong)' }) as CSSProperties;
